import React, { ChangeEvent, useMemo, useState } from 'react';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import _ from 'lodash';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { AddDocumentTemplateRequestRequest } from 'generated/api';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { IconButton, TextareaAutosize } from '@material-ui/core';
import Icon from 'components/Utils/NewIcon';
import DefaultButton from 'components/Utils/NewDefaultButton';
import UploadFile from 'components/Utils/UploadFile';

function RequestDocumentTemplateModal({
  onClose,
  defaultCategory,
}: {
  onClose: () => void;
  defaultCategory: string | undefined;
}) {
  const dispatch = useAppDispatch();
  const categories = useAppSelector((state) => state.templates.templateCategories);
  const templates = useAppSelector((state) => state.templates.templates);
  const categoryNames = useMemo(
    () => categories.filter((it) => it.active).map((it) => it.name),
    [categories],
  );
  const [createNewCategory, setCreateNewCategory] = useState(false);
  const [createNewSubCategory, setCreateNewSubCategory] = useState(false);
  const [createNewSubsubCategory, setCreateNewSubsubCategory] = useState(false);
  const [description, setDescription] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>(defaultCategory);
  const [subcategory, setSubCategory] = useState<string | undefined>();
  const [subsubcategory, setSubsubCategory] = useState<string | undefined>();
  const [file, setFile] = useState<Blob | undefined>(undefined);
  const [instructions, setInstructions] = useState<string | undefined>();
  const isLoading = useIsLoading(api.Templates.addDocumentTemplateRequest);

  const categoryMap = useMemo(
    () =>
      _(categories)
        .filter('active')
        .groupBy('name')
        .mapValues((it) => it[0]!.id)
        .value(),
    [categories],
  );

  const subcategories = useMemo(() => {
    return _(templates)
      .filter((it) => !!category && it.category_id === categoryMap[category])
      .map((it) => it.secondary_name?.split('>')?.[0])
      .compact()
      .uniq()
      .value() as string[];
  }, [category, categoryMap, templates]);

  const subsubcategories = useMemo(() => {
    return _(templates)
      .filter((it) => !!category && it.category_id === categoryMap[category])
      .map((it) => it.secondary_name?.split('>') as string[])
      .compact()
      .filter(([sub]) => sub === subcategory)
      .map(([, subsub]) => subsub)
      .compact()
      .uniq()
      .value();
  }, [templates, category, subcategory, categoryMap]);

  const canSubmit = category && file && description;

  async function onSubmit() {
    if (!description || !file) {
      return;
    }
    let categoryId: string | undefined = category && categoryMap[category];
    if (createNewCategory || categoryNames.length === 0) {
      const newCategory = await dispatch(
        api.Templates.addDocumentTemplateCategory({
          documentTemplateCategory: {
            name: category!,
          },
        }),
      );
      categoryId = newCategory?.id;
    }

    if (!categoryId) {
      return;
    }

    const data: AddDocumentTemplateRequestRequest = {
      description,
      categoryId,
      subcategory,
      subsubcategory,
      file,
      instructions,
    };

    dispatch(api.Templates.addDocumentTemplateRequest(data)).finally(onClose);
  }

  function onCancel() {
    onClose();
  }

  return (
    <div className="request-template-modal-content">
      <DefaultTextField
        label="Name"
        name="description"
        onChange={(ev: ChangeEvent<HTMLInputElement>) => setDescription(ev.target.value)}
      />
      <div className="inline-field">
        {createNewCategory || categoryNames.length === 0 ? (
          <DefaultTextField
            label="Category"
            name="category"
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setCategory(ev.target.value)}
          />
        ) : (
          <>
            <DefaultSelectField
              inputs={categoryNames}
              name="category"
              label="Category"
              value={category}
              onChange={(ev: ChangeEvent<HTMLInputElement>) => setCategory(ev.target.value)}
            />
            <IconButton onClick={() => setCreateNewCategory(true)}>
              <Icon src="circle-plus" height="21px" />
            </IconButton>
          </>
        )}
      </div>
      <div className="inline-field">
        {createNewSubCategory || (subcategories || []).length === 0 ? (
          <DefaultTextField
            label="Subcategory"
            name="subcategory"
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setSubCategory(ev.target.value)}
          />
        ) : (
          <>
            <DefaultSelectField
              inputs={subcategories || [].map((it) => parseSecondaryName(it)[0])}
              name="subcategory"
              label="Subcategory"
              onChange={(ev: ChangeEvent<HTMLInputElement>) => setSubCategory(ev.target.value)}
            />
            <IconButton onClick={() => setCreateNewSubCategory(true)}>
              <Icon src="circle-plus" height="20px" />
            </IconButton>
          </>
        )}
      </div>
      {subcategory && (
        <div className="inline-field">
          {createNewSubsubCategory || subsubcategories.length === 0 ? (
            <DefaultTextField
              label="Subsubcategory"
              name="subsubcategory"
              onChange={(ev: ChangeEvent<HTMLInputElement>) => setSubsubCategory(ev.target.value)}
            />
          ) : (
            <>
              <DefaultSelectField
                inputs={subsubcategories}
                name="subcategory"
                label="Subsubcategory"
                onChange={(ev: ChangeEvent<HTMLInputElement>) => setSubsubCategory(ev.target.value)}
              />
              <IconButton onClick={() => setCreateNewSubsubCategory(true)}>
                <Icon src="circle-plus" height="20px" />
              </IconButton>
            </>
          )}
        </div>
      )}
      <div className="small-title instructions">Instructions</div>
      <TextareaAutosize
        placeholder="Instructions"
        onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => setInstructions(ev.target.value)}
      />
      <div className="small-title content">Upload PDF</div>
      <UploadFile title="" accept="application/pdf" onDrop={setFile} />
      <div className="buttons">
        <DefaultButton name="Cancel" className="red" disabled={isLoading} onClick={onCancel} />
        <DefaultButton name="Submit" disabled={!canSubmit} loading={isLoading} onClick={onSubmit} />
      </div>
    </div>
  );
}

export default RequestDocumentTemplateModal;

function parseSecondaryName(string: string) {
  return string.split('>');
}
